export class Util {
  static getCumulativeStrings(inputString: string, delimiter: string): string[] {
    const substrings = inputString.split(delimiter);
    const cumulativeStrings: string[] = [];
    let currentString = "";

    cumulativeStrings.push(delimiter);
    for ( let i = 1; i < substrings.length; i++) {
      const substring = substrings[i];
      currentString += delimiter;
      currentString += substring;
      cumulativeStrings.push(currentString);
    }
    return cumulativeStrings;
  }

  static getParentItem(inputString: string, delimiter: string): string {
    const substrings = inputString.split(delimiter);
    let ret = '';
    for ( let i = 1; i < substrings.length - 1; i++) {
      const part = substrings[i];
      ret += delimiter + part;
    }
    if ( ret === '') {
      return delimiter;
    }
    return ret;
  }

  static getLastItem(inputString: string, delimiter: string): string {
    const substrings = inputString.split(delimiter);
    return substrings[substrings.length - 1];
  }

  /**
   * UTF-8 safe base64 encode.
   *
   * Several Project endpoints take their string arguments as base64 inside a
   * URL *path segment* and the server decodes with
   * `Buffer.from(param, 'base64')`. Bare `btoa()` throws
   * `InvalidCharacterError` on any code point above U+00FF, so a Project or
   * folder name containing an accent, an em dash, or any non-Latin script
   * would blow up the caller rather than round-trip. Encode to UTF-8 bytes
   * first so what the server decodes is the UTF-8 the client meant.
   *
   * NOTE: the output is *standard* base64 and can therefore contain `+`, `/`
   * and `=`. The server route patterns are written as `([A-Za-z0-9+/=]+)` to
   * accept exactly that, so it must NOT be percent-encoded here — `%2F` does
   * not match that character class and the request would 404.
   */
  static toBase64(value: string): string {
    const text = ( value === null || value === undefined ) ? '' : String(value);
    try {
      const utf8 = new TextEncoder().encode(text);
      let binary = '';
      for ( const byte of utf8 ) {
        binary += String.fromCharCode(byte);
      }
      return btoa(binary);
    } catch ( e ) {
      // Last-resort fallback; better a mangled name than a thrown exception
      // that strands the whole operation.
      console.error(`Util.toBase64 failed for [${text}]`, e);
      return btoa(unescape(encodeURIComponent(text)));
    }
  }

  /**
   * Canonical form of a project-folder path for comparison purposes.
   *
   * `ProjectFolders.getFlattened()` and `Project.folder` both use a leading
   * `/` with `/` as the separator. The tree root is represented
   * inconsistently across the code base as `''` and as `'/'`; both normalize
   * to `''` here so the two spellings compare equal.
   */
  static normalizeFolderPath(path: string): string {
    if ( typeof path !== 'string' ) {
      return '';
    }
    let s = path.trim();
    while ( s.length > 1 && s.endsWith('/') ) {
      s = s.substring(0, s.length - 1);
    }
    if ( s === '/' ) {
      s = '';
    }
    return s;
  }

  /**
   * Is `candidate` the folder `folder` itself, or any descendant of it?
   *
   * This is a **path-segment** test, deliberately not `startsWith`. Plain
   * `candidate.startsWith(folder)` reports `/Testing/x` as living inside
   * `/Test`, which silently mis-scopes both the "is this folder empty?" guard
   * before a folder delete and the set of Projects swept up by a folder move.
   * A folder called `Test` sitting next to one called `Testing` is completely
   * ordinary, so this is not a hypothetical.
   *
   * The tree root (`''` or `'/'`) contains everything, by definition.
   */
  static isFolderAtOrUnder(candidate: string, folder: string): boolean {
    if ( typeof candidate !== 'string' || typeof folder !== 'string' ) {
      return false;
    }
    const c = Util.normalizeFolderPath(candidate);
    const f = Util.normalizeFolderPath(folder);
    if ( f === '' ) {
      return true;
    }
    if ( c === f ) {
      return true;
    }
    return c.startsWith(f + '/');
  }

  /**
   * Token-by-token comparison of two strings, for rendering "what changed".
   *
   * Both sides are coerced before use. They used to be dereferenced directly,
   * so a single `undefined` -- routinely produced by an imported address with
   * no second line -- threw a TypeError. Because the only caller invoked this
   * from inside `*ngFor`, that throw happened during change detection, which
   * is the worst possible place for one: it abandons the pass with the DOM
   * half-updated. See `AddressSuggestion` for what that looked like on screen.
   *
   * A missing side is not an error here. It means "this field is absent", and
   * the honest comparison is against the empty string.
   */
  static getSameAndDifferentFragments(textA: string, textB: string, separatorCharacter: string): TextFragment[] {
    const safeTextA = ( textA === undefined || textA === null ) ? '' : String(textA);
    const safeTextB = ( textB === undefined || textB === null ) ? '' : String(textB);
    const tokensA = safeTextA.trim().split(separatorCharacter).filter(token => token !== '');
    const tokensB = safeTextB.trim().split(separatorCharacter).filter(token => token !== '');

    const fragments: TextFragment[] = [];
    let indexA = 0;
    let indexB = 0;

    while (indexA < tokensA.length || indexB < tokensB.length) {
      if (tokensA[indexA] === tokensB[indexB]) {
        // Found a match or a sequence of matches
        let sameTextA = '';
        let sameTextB = '';
        const sameStartIndexA = indexA;
        const sameStartIndexB = indexB;

        while (indexA < tokensA.length && indexB < tokensB.length && tokensA[indexA] === tokensB[indexB]) {
          sameTextA += (sameTextA.length > 0 ? separatorCharacter : '') + tokensA[indexA];
          sameTextB += (sameTextB.length > 0 ? separatorCharacter : '') + tokensB[indexB];
          indexA++;
          indexB++;
        }
        fragments.push({ textA: sameTextA, textB: sameTextB, different: false });
      } else {
        // Found a difference or a sequence of differences
        let differentTextA = '';
        let differentTextB = '';

        // Collect tokens from A until we find the next match in B
        let nextMatchIndexA = -1;
        let nextMatchIndexB = -1;
        let searchIndexA = indexA;
        let searchIndexB = indexB;

        while (searchIndexA < tokensA.length) {
          searchIndexB = indexB;
          while (searchIndexB < tokensB.length) {
            if (tokensA[searchIndexA] === tokensB[searchIndexB]) {
              nextMatchIndexA = searchIndexA;
              nextMatchIndexB = searchIndexB;
              break;
            }
            searchIndexB++;
          }
          if (nextMatchIndexA !== -1) {
            break;
          }
          searchIndexA++;
        }

        // Build the different fragment
        if (nextMatchIndexA === -1) {
          // No more matches, so the rest is different
          while (indexA < tokensA.length) {
            differentTextA += (differentTextA.length > 0 ? separatorCharacter : '') + tokensA[indexA++];
          }
          while (indexB < tokensB.length) {
            differentTextB += (differentTextB.length > 0 ? separatorCharacter : '') + tokensB[indexB++];
          }
        } else {
          // Collect tokens until the next match is found
          while (indexA < nextMatchIndexA) {
            differentTextA += (differentTextA.length > 0 ? separatorCharacter : '') + tokensA[indexA++];
          }
          while (indexB < nextMatchIndexB) {
            differentTextB += (differentTextB.length > 0 ? separatorCharacter : '') + tokensB[indexB++];
          }
        }

        if (differentTextA || differentTextB) {
          fragments.push({ textA: differentTextA, textB: differentTextB, different: true });
        }
      }
    }

    return fragments;
  }

  static getDateTimestamp(date: Date): number {
    try {
      if ( date && date instanceof Date ) {
        return date.getTime();
      }
    } catch ( e ) {
      console.log(`Error processing date[${date}]`);
    }
    return 0;
  }

  static isValidTime(date: Date): boolean {
    try {
      if ( date && date instanceof Date && date.getTime() > 0 ) {
        return true;
      } else {
        const d = new Date(date);
        if ( d && d instanceof Date && d.getTime() > 0 ) {
          return true;
        }
      }
      return false;
    } catch ( e ) {
      console.log(`Error processing date[${date}]`);
    }
    return false;
  }

  static deepEqual(obj1: any, obj2: any): boolean {
    if (obj1 === obj2) {
      return true;
    }

    if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) {
      return false;
    }

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    for (const key of keys1) {
      if (!keys2.includes(key) || !Util.  deepEqual(obj1[key], obj2[key])) {
        return false;
      }
    }

    return true;
  }

}

export class TextFragment {
  textA: string;
  textB: string;
  different: boolean;
}
